export const getIntroduction = (date = new Date()) => {
  const hour = date.getHours()
  let greeting = 'Good evening'

  if (hour < 12) greeting = 'Good morning'
  else if (hour < 18) greeting = 'Good afternoon'

  return `${greeting}. What would you like to know about your projects today?`
}

export const presentationTiming = {
  firstStatusDelay: 650,
  statusInterval: 1250,
  responseStartDelay: 950,
  wordDelay: 38,
  wordDelayVariation: 9,
}

export const starterSuggestions = [
  {
    id: 'weather-tampere',
    prompt: "🌤️ What's the weather like at the Tampere construction site?",
    statuses: [
      'Resolving the Tampere site coordinates…',
      'Requesting current weather conditions…',
      'Assessing the impact on scheduled outdoor work…',
    ],
    response: `Tampere Keskusta

12°C · Light rain
Wind: 14 km/h
Rain expected until approximately 13:00

Outdoor concrete work may be affected this morning. Consider moving weather-sensitive work to the afternoon.`,
  },
  {
    id: 'workers-on-site',
    prompt: '👷 How many workers are currently on site?',
    statuses: [
      "Loading today's site attendance records…",
      'Matching active shifts to project sites…',
      'Calculating the current on-site headcount…',
    ],
    response: `There are 47 employees currently working across 6 active construction sites.

Tampere Keskusta — 12
Hervanta Offices — 9
Pirkkala Housing — 8
Nokia Logistics — 7
Ylöjärvi School — 6
Lempäälä Renovation — 5

Three additional employees have not started their workday yet.`,
  },
  {
    id: 'missing-hours',
    prompt: "⏱️ Who hasn't submitted their working hours today?",
    statuses: [
      "Loading today's planned shifts…",
      'Comparing shifts with submitted work reports…',
      'Identifying missing entries…',
    ],
    response: `3 employees still have missing work reports today:

Mikko Virtanen — Tampere Keskusta
Jari Nieminen — Hervanta Offices
Antti Laine — Pirkkala Housing

Their site supervisors should review the missing entries before the daily reporting cutoff.`,
  },
  {
    id: 'project-progress',
    prompt: "📊 Show me today's project progress.",
    statuses: [
      "Collecting today's project updates…",
      'Comparing reported work with project plans…',
      'Calculating progress and schedule variance…',
    ],
    response: `Today's overall progress is on track.

6 active projects
47 employees working
312 reported work hours
3 missing work reports

Tampere Keskusta — 74% complete · On schedule
Hervanta Offices — 61% complete · 2 days behind
Pirkkala Housing — 48% complete · On schedule

Hervanta Offices is the only active project currently outside its planned schedule.`,
  },
  {
    id: 'project-schedule',
    prompt: '📅 Are any projects falling behind schedule?',
    statuses: [
      'Loading active project schedules…',
      'Comparing planned and reported progress…',
      'Identifying delayed work phases…',
    ],
    response: `1 of 6 active projects is currently behind schedule.

Hervanta Offices — 2 days behind
Planned progress: 64%
Reported progress: 61%

Electrical installation is the main delayed work phase. The remaining active projects are on schedule.`,
  },
  {
    id: 'reports-attention',
    prompt: '📄 Which project reports still need attention?',
    statuses: [
      'Loading current project report statuses…',
      'Checking approvals, corrections, and missing entries…',
      'Prioritizing reports requiring attention…',
    ],
    response: `5 project reports still need attention:

2 awaiting approval — Tampere Keskusta
1 returned for correction — Hervanta Offices
2 missing daily entries — Pirkkala Housing

The oldest pending report was submitted yesterday at 16:42.`,
  },
]

export const defaultKeywordResponses = [
  {
    id: 'keyword-project',
    keyword: 'project',
    response: `There are 6 active projects at the moment.

Tampere Keskusta is 74% complete, Hervanta Offices is 61% complete, and Pirkkala Housing is 48% complete. Hervanta Offices is currently 2 days behind schedule.`,
  },
]

export const defaultKeywordStatuses = [
  'Searching for records related to “{keyword}”…',
  'Reviewing the matching project data…',
  'Preparing the response…',
]

const genericResponse = {
  statuses: [
    'Identifying the relevant project data…',
    'Reviewing the available records…',
    'Preparing the response…',
  ],
  response: `I couldn't find enough project data to answer that request.

Try asking about current site attendance, missing work reports, project progress, schedules, report statuses, or weather conditions at a project site.`,
}

const normalizeMessage = (message) =>
  String(message || '')
    .replace(/^[^\p{L}\p{N}]+/u, '')
    .trim()
    .toLocaleLowerCase()

export const getMockResponse = (
  message,
  suggestions = starterSuggestions,
  keywordResponses = defaultKeywordResponses,
  keywordStatuses = defaultKeywordStatuses,
) => {
  const normalizedMessage = normalizeMessage(message)
  const conversation = suggestions.find(
    (item) => normalizeMessage(item.prompt) === normalizedMessage,
  )

  if (conversation) return {...conversation}

  const keywordResponse = keywordResponses.find((item) => {
    const keyword = normalizeMessage(item.keyword)
    return keyword && item.response.trim() && normalizedMessage.includes(keyword)
  })

  if (keywordResponse) {
    const keyword = keywordResponse.keyword.trim()

    return {
      statuses: keywordStatuses
        .map((status) => status.replace(/\{keyword\}/gi, keyword).trim())
        .filter(Boolean),
      response: keywordResponse.response.trim(),
    }
  }

  return {...genericResponse}
}
